<template>
  <q-page class="sede-page">
    <div class="sede-container">
      <!-- Loading state -->
      <template v-if="loading">
        <div>
          <q-skeleton type="text" width="160px" dark style="font-size: 1.75rem;" />
          <q-skeleton type="text" width="280px" dark style="margin-top: 4px;" />
        </div>
        <div class="sede-grid" style="margin-top: 24px;">
          <q-skeleton v-for="n in 4" :key="n" type="rect" height="180px" dark style="border-radius: 12px;" />
        </div>
        <q-skeleton type="rect" height="120px" dark style="border-radius: 12px; margin-top: 24px;" />
      </template>

      <!-- Error state -->
      <div v-else-if="error" class="sede-empty">
        <q-icon name="error_outline" size="48px" style="color: var(--oa-danger);" />
        <p class="empty-text">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 8px; margin-top: 16px;"
          @click="fetchData"
        />
      </div>

      <!-- Loaded state -->
      <template v-else>
        <!-- Header -->
        <div class="sede-header">
          <h1 class="sede-title">Mis Sedes</h1>
          <p class="sede-subtitle">Gestion de sedes de El Templo Calistenia</p>
        </div>

        <!-- Sedes grid -->
        <div class="sede-grid">
          <div v-for="sede in sedes" :key="sede.id" class="sede-card">
            <div class="sede-card-top">
              <div class="sede-card-name-row">
                <div class="sede-icon-wrap">
                  <q-icon name="store" size="16px" style="color: var(--oa-gold-full);" />
                </div>
                <h3 class="sede-card-name">{{ sede.name }}</h3>
              </div>
              <q-badge
                :label="statusLabel(sede.status)"
                :style="{
                  background: sede.status === 'active'
                    ? 'color-mix(in srgb, var(--oa-gold-full) 15%, transparent)'
                    : 'var(--oa-bg-3)',
                  color: sede.status === 'active' ? 'var(--oa-gold-full)' : 'var(--oa-text-secondary)',
                  fontSize: '11px',
                  padding: '2px 8px',
                  borderRadius: '6px',
                }"
              />
            </div>

            <div class="sede-details-grid">
              <div>
                <p class="sede-detail-label">Codigo</p>
                <p class="sede-detail-value">{{ sede.code }}</p>
              </div>
              <div>
                <p class="sede-detail-label">Region</p>
                <p class="sede-detail-value">{{ sede.regionName || '--' }}</p>
              </div>
              <div>
                <p class="sede-detail-label">Ciudad</p>
                <p class="sede-detail-value">{{ sede.city }}</p>
              </div>
              <div>
                <p class="sede-detail-label">Provincia</p>
                <p class="sede-detail-value">{{ sede.province }}</p>
              </div>
            </div>

            <div class="sede-stats-row">
              <div class="sede-stat-cell">
                <p class="sede-stat-label">AAP</p>
                <p class="sede-stat-val sede-stat-gold">{{ sede.currentAap }}</p>
              </div>
              <div class="sede-stat-cell">
                <p class="sede-stat-label">Cap. Max</p>
                <p class="sede-stat-val">{{ sede.maxCapacity || '--' }}</p>
              </div>
              <div class="sede-stat-cell">
                <p class="sede-stat-label">Ocupacion</p>
                <p class="sede-stat-val">
                  {{ sede.maxCapacity ? Math.round((sede.currentAap / sede.maxCapacity) * 100) + '%' : '--' }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Partnership types -->
        <div class="sede-section-card">
          <h3 class="section-title">Tipos de Partnership</h3>
          <div class="partnership-grid">
            <div
              v-for="p in partnerships"
              :key="p.type"
              class="partnership-item"
              :style="{
                background: p.bg,
                border: '1px solid ' + p.border,
              }"
            >
              <p class="partnership-label" :style="{ color: p.textColor }">
                Tipo {{ p.type }} -- {{ p.label }}
              </p>
              <p class="partnership-desc">{{ p.desc }}</p>
            </div>
          </div>
        </div>

        <!-- Pipeline (best-effort) -->
        <div v-if="pipeline" class="sede-section-card">
          <div class="pipeline-header">
            <h3 class="section-title">Pipeline de Incorporacion</h3>
            <span class="pipeline-summary">
              {{ totalActive }} activos | {{ pipeline.completado || 0 }} completados
            </span>
          </div>
          <div class="pipeline-stages">
            <div
              v-for="(stage, i) in pipelineStages"
              :key="stage.key"
              class="pipeline-stage"
            >
              <p class="pipeline-stage-phase">Fase {{ i + 1 }}</p>
              <p class="pipeline-stage-label">{{ stage.label }}</p>
              <p class="pipeline-stage-count">{{ pipeline[stage.key] || 0 }}</p>
              <p class="pipeline-stage-unit">candidatos</p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { api } from 'boot/axios';

// ---------- Types ----------
interface Sede {
  id: string;
  name: string;
  code: string;
  city: string;
  province: string;
  status: string;
  currentAap: number;
  maxCapacity?: number;
  regionName?: string;
}

interface PipelineData {
  i1_cultura?: number;
  i2_observacion?: number;
  i3_codireccion?: number;
  i4_primera_solo?: number;
  completado?: number;
  [key: string]: number | undefined;
}

// ---------- State ----------
const loading = ref(true);
const error = ref<string | null>(null);
const sedes = ref<Sede[]>([]);
const pipeline = ref<PipelineData | null>(null);

// ---------- Constants ----------
const partnerships = [
  {
    type: 'A',
    label: 'Franquicia',
    desc: 'Uso completo de marca, sistema y soporte. Canon mensual + % de ingresos.',
    bg: 'color-mix(in srgb, var(--oa-gold-full) 5%, transparent)',
    border: 'color-mix(in srgb, var(--oa-gold-full) 20%, transparent)',
    textColor: 'var(--oa-gold-full)',
  },
  {
    type: 'B',
    label: 'Licencia',
    desc: 'Acceso al metodo y formacion. Sin uso de marca en espacio fisico.',
    bg: 'var(--oa-bg-3)',
    border: 'color-mix(in srgb, var(--oa-border) 20%, transparent)',
    textColor: 'var(--oa-text-primary)',
  },
  {
    type: 'C',
    label: 'Afiliado',
    desc: 'Solo acceso a formacion online. Sin vinculo operativo.',
    bg: 'color-mix(in srgb, var(--oa-bg-3) 30%, transparent)',
    border: 'color-mix(in srgb, var(--oa-border) 10%, transparent)',
    textColor: 'var(--oa-text-secondary)',
  },
];

const pipelineStages = [
  { key: 'i1_cultura', label: 'I1 -- Cultura' },
  { key: 'i2_observacion', label: 'I2 -- Observacion' },
  { key: 'i3_codireccion', label: 'I3 -- Co-direccion' },
  { key: 'i4_primera_solo', label: 'I4 -- Primera Solo' },
];

// ---------- Computed ----------
const totalActive = computed(() => {
  if (!pipeline.value) return 0;
  return (pipeline.value.i1_cultura || 0) +
    (pipeline.value.i2_observacion || 0) +
    (pipeline.value.i3_codireccion || 0) +
    (pipeline.value.i4_primera_solo || 0);
});

// ---------- Helpers ----------
function statusLabel(status: string): string {
  switch (status) {
    case 'active': return 'Activa';
    case 'pilot': return 'Piloto';
    case 'suspended': return 'Suspendida';
    default: return 'Inactiva';
  }
}

// ---------- Data Fetching ----------
async function fetchData() {
  loading.value = true;
  error.value = null;
  try {
    const { data: res } = await api.get('/ot');
    sedes.value = res.sedes || [];

    // Pipeline is optional -- incorporacion API may not exist yet
    try {
      const { data: pipeRes } = await api.get('/incorporacion/pipeline');
      pipeline.value = pipeRes;
    } catch {
      pipeline.value = null;
    }
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Error al cargar sedes';
    error.value = msg;
  } finally {
    loading.value = false;
  }
}

// ---------- Lifecycle ----------
onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.sede-page {
  padding: 24px;
}

.sede-container {
  max-width: 1200px;
  margin: 0 auto;
}

.sede-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 12px;
}

.empty-text {
  color: var(--oa-text-muted);
  font-size: 14px;
}

/* Header */
.sede-header {
  margin-bottom: 24px;
}

.sede-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.sede-subtitle {
  font-size: 0.875rem;
  color: var(--oa-text-muted);
  margin: 4px 0 0;
}

/* Sedes grid */
.sede-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

@media (max-width: 768px) {
  .sede-grid {
    grid-template-columns: 1fr;
  }
}

.sede-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 20px;
  transition: border-color 0.2s ease;
}

.sede-card:hover {
  border-color: color-mix(in srgb, var(--oa-gold-full) 30%, transparent);
}

.sede-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.sede-card-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sede-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
}

.sede-card-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.sede-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 16px;
}

.sede-detail-label {
  font-size: 0.75rem;
  color: var(--oa-text-muted);
  margin: 0;
}

.sede-detail-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 2px 0 0;
}

.sede-stats-row {
  display: flex;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
}

.sede-stat-cell {
  flex: 1;
  text-align: center;
}

.sede-stat-label {
  font-size: 0.6875rem;
  color: var(--oa-text-muted);
  margin: 0;
}

.sede-stat-val {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--oa-text-secondary);
  margin: 4px 0 0;
}

.sede-stat-gold {
  color: var(--oa-gold-full);
}

/* Section card */
.sede-section-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 16px;
}

/* Partnership */
.partnership-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

@media (max-width: 768px) {
  .partnership-grid {
    grid-template-columns: 1fr;
  }
}

.partnership-item {
  padding: 16px;
  border-radius: 12px;
}

.partnership-label {
  font-weight: 700;
  font-size: 0.9375rem;
  margin: 0;
}

.partnership-desc {
  font-size: 0.875rem;
  color: var(--oa-text-muted);
  margin: 8px 0 0;
}

/* Pipeline */
.pipeline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.pipeline-header .section-title {
  margin: 0;
}

.pipeline-summary {
  font-size: 0.75rem;
  color: var(--oa-text-muted);
}

.pipeline-stages {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 8px;
}

.pipeline-stage {
  flex-shrink: 0;
  width: 160px;
  padding: 12px;
  background: color-mix(in srgb, var(--oa-gold-full) 5%, transparent);
  border: 1px solid color-mix(in srgb, var(--oa-gold-full) 15%, transparent);
  border-radius: 12px;
  text-align: center;
}

.pipeline-stage-phase {
  font-size: 0.6875rem;
  color: var(--oa-text-muted);
  margin: 0;
}

.pipeline-stage-label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 4px 0 0;
}

.pipeline-stage-count {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--oa-gold-full);
  margin: 8px 0 0;
}

.pipeline-stage-unit {
  font-size: 0.6875rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}
</style>
